/**
 * Reduces a selected industry-code list to its MINIMAL COVERING SET: the codes
 * that have no other selected code as an ancestor. Selecting `C` and its whole
 * subtree collapses back to just `['C']`.
 *
 * Why this exists: `toggleIndustryCodeSelection` cascades a selection to every
 * descendant, so one section is a selection of hundreds of codes (Manufacturing
 * alone is 351). Letting that expansion out of the picker broke two things —
 * GA4 caps an event-parameter value at 100 characters, so only ~13 codes ever
 * survived, and CloudFront rejects a URL over 8192 bytes with 414, which two
 * selected sections already exceed.
 *
 * Ancestry is plain string prefixing. The backend builds every level by slicing
 * one source code (see `nomenclature.js`), so levels nest as
 * `C` -> `C10` -> `C101` -> `C1011` -> `C10110`, and a section letter can only
 * appear at index 0. No tree lookup, no store access.
 *
 * Idempotent: collapsing an already-collapsed list returns it unchanged. That is
 * what lets result links and saved projects stored in the old expanded format
 * keep working with no migration.
 *
 * @param {string[]} codes - selected industry codes, expanded or already collapsed
 * @returns {string[]} the covering set, in the order the codes were given
 */
export const collapseIndustryCodes = (codes = []) => {
  if (!Array.isArray(codes)) {
    return [];
  }

  const selected = new Set(codes);
  const seen = new Set();
  const covering = [];

  codes.forEach((code) => {
    if (typeof code !== 'string' || !code || seen.has(code)) {
      return;
    }

    for (let end = 1; end < code.length; end += 1) {
      if (selected.has(code.slice(0, end))) {
        return;
      }
    }

    seen.add(code);
    covering.push(code);
  });

  return covering;
};

/**
 * The distinct top-level sections of a code list, as their single letters.
 *
 * There are only 21 sections, so the joined result is at most 41 characters and
 * can never hit the GA4 parameter cap — which is what makes it usable as the
 * reporting dimension for "which sectors do users search".
 *
 * @param {string[]} codes - industry codes, expanded or collapsed
 * @returns {string[]} sorted, de-duplicated section letters
 */
export const industrySectionsFromCodes = (codes = []) => {
  if (!Array.isArray(codes)) {
    return [];
  }

  return [
    ...new Set(codes.filter((code) => typeof code === 'string' && code).map((code) => code[0]))
  ].sort((a, b) => a.localeCompare(b));
};
